"use client";

import { useEffect, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import kebabCase from "lodash/kebabCase";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import { generateUrlByQuery } from "@stadium/helpers/src/dom";

import { useMutation } from "@stadium/services/apollo-client";
import { showErrorToast } from "@stadium/services/react-toastify";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import { LOGIN_CALLBACK_ROUTE, useAuth0V2 } from "@stadium/services/auth0";

import useScript from "@stadium/hooks/lib/use-scripts/use-script";
import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";

import Button from "@stadium/shared/src/components/shared/button";
import PackageAssociatedModal from "components/onboarding-packages/purchase-package/package-associated-modal";

import {
  CREATE_CHILD_COMPANY,
  VALIDATE_COMPANY_SUBSCRIPTION,
} from "@stadium/shared/src/graphql/mutations/package";

import { COMPANY_SPACE_URL } from "constants/routes";
import { CHARGEBEE, CHARGEBEE_CB_SITE } from "@stadium/constants/scripts";
import { BASE_PATH } from "constants/env-variables";
import {
  ALREADY_SUBSCRIBED_MODAL,
  CHARGEBEE_FLOW,
  CHARGEBEE_MODAL,
  CHARGEBEE_SERVICES,
  CHECKOUT,
  PAYMENT_ERROR,
  PAYMENT_SUCCESS,
} from "constants/june-so";
import { WORKSPACE_SLUG_REQUEST_HEADER_KEY } from "@stadium/constants";

const DEFAULT_OBJ = {};

type PurchasePackageBtnTypes = {
  packageSlug?: string;
  packageName: string;
  containerClass?: string;
  juneSoProps?: Record<string, string>;
  btnText?: string;
  btnStyleType?: string;
  packageIntent?: string;
  onSuccess?: () => void | null;
  workspaceSlug?: string;
  companyNumber?: string;
};

const PurchasePackageBtn = ({
  packageSlug = "",
  packageName = "",
  containerClass,
  juneSoProps = DEFAULT_OBJ,
  btnText,
  btnStyleType = "link",
  packageIntent,
  onSuccess = null,
  workspaceSlug,
  companyNumber,
}: PurchasePackageBtnTypes) => {
  const { query, replace } = useRouter();
  const { purchasePkg } = query || DEFAULT_OBJ;

  const { isAuthenticated, loginWithRedirect } = useAuth0V2();

  const { currentUserData } = useCurrentUserContext();
  const { trackEvent } = useJuneSoEventTracking({ services: CHARGEBEE_SERVICES });
  const elementPosition = kebabCase(packageName);

  const { data: { instance: chargebee } = DEFAULT_OBJ, loading } = useScript(CHARGEBEE);

  const [createChildCompany] = useMutation(CREATE_CHILD_COMPANY);
  const [validateCompanySubscription] = useMutation(
    VALIDATE_COMPANY_SUBSCRIPTION,
    workspaceSlug && {
      context: {
        headers: {
          [WORKSPACE_SLUG_REQUEST_HEADER_KEY]: workspaceSlug,
        },
      },
    },
  );

  const [modalData, setModalData] = useState(null);

  const redirectToLogin = () => {
    loginWithRedirect({
      redirectUri: window.location.origin + BASE_PATH + LOGIN_CALLBACK_ROUTE,
      appState: {
        returnTo: generateUrlByQuery({
          pathname: window.location.pathname,
          query: { ...query, purchasePkg: packageSlug },
        }),
      },
      signUp: true,
    });
  };

  const onPurchaseSuccess = async () => {
    const { data } = await validateCompanySubscription();
    const { success, errors } = data?.validateCompanySubscription || DEFAULT_OBJ;

    if (errors?.length) {
      errors.forEach((err) => showErrorToast({ message: err }));
    }
    if (onSuccess) {
      onSuccess();
    }
    return success;
  };

  const openChargebeeModal = ({ number, packageIntentMappingKey = "" }) => {
    trackEvent({
      elementPosition,
      target: CHARGEBEE_MODAL,
      action: "opened",
    });
    const cbInstance = chargebee.init({
      site: CHARGEBEE_CB_SITE,
      iframeOnly: true,
      enableGATracking: true,
      enableGTMTracking: true,
    });
    cbInstance.setCheckoutCallbacks(() => {
      let purchaseSuccess = false;

      return {
        success: async () => {
          purchaseSuccess = await onPurchaseSuccess();
          trackEvent({
            elementPosition,
            target: CHARGEBEE_FLOW,
            action: PAYMENT_SUCCESS,
          });
        },
        close: () => {
          trackEvent({
            elementPosition,
            target: CHARGEBEE_MODAL,
            action: "closed",
          });
          if (purchaseSuccess) {
            if (onSuccess) {
              return onSuccess({ redirect: true, shouldFetchData: false });
            }
            window.location.href = `${COMPANY_SPACE_URL}/${number}/dashboard`;
          }
        },
        error: () => {
          trackEvent({
            elementPosition,
            target: CHARGEBEE_FLOW,
            action: PAYMENT_ERROR,
          });
        },
      };
    });

    cbInstance.setPortalCallbacks({
      close: async () => {
        const isSuccess = await onPurchaseSuccess();
        if (isSuccess) {
          if (onSuccess) {
            return onSuccess();
          }
          window.location.href = `${COMPANY_SPACE_URL}/${number}/dashboard`;
        }
      },
      error: () => {
        trackEvent({
          elementPosition,
          target: CHARGEBEE_FLOW,
          action: PAYMENT_ERROR,
        });
      },
      subscriptionChanged: () => {
        trackEvent({
          elementPosition,
          target: CHARGEBEE_FLOW,
          action: PAYMENT_SUCCESS,
        });
      },
    });

    const { firstName, lastName, email } = currentUserData || DEFAULT_OBJ;
    const customer = {
      id: companyNumber || number,
      first_name: firstName,
      last_name: lastName,
      email,
    };

    const cart = cbInstance.getCart();
    const product = cbInstance.initializeProduct(packageIntentMappingKey);

    product.setIsItemsModel(true);
    cart.setCustomer(customer);
    cart.replaceProduct(product);
    cart.proceedToCheckout();
  };

  const handleCheckout = async () => {
    if (packageIntent) {
      return openChargebeeModal({ packageIntentMappingKey: packageIntent });
    }

    const { data } = await createChildCompany({ variables: { input: packageSlug } });
    const { childCompany, success, errors } = data?.createChildCompany || DEFAULT_OBJ;

    if (success && childCompany) {
      openChargebeeModal(childCompany);
    }
    if (!success && childCompany) {
      trackEvent({
        elementPosition,
        target: ALREADY_SUBSCRIBED_MODAL,
        action: "opened",
      });
      setModalData(childCompany);
    }
    if (errors?.length) {
      trackEvent({
        elementPosition,
        target: CHECKOUT,
        action: "error",
      });
      errors.forEach((err) => showErrorToast({ message: <>{ReactHtmlParser(err)}</> }));
    }
  };

  useEffect(() => {
    if (purchasePkg === packageSlug && chargebee && isAuthenticated) {
      const url = new URL(window.location.href);
      url.searchParams.delete("purchasePkg");
      replace(url);
      handleCheckout();
    }
  }, [purchasePkg, chargebee, isAuthenticated]);

  return (
    <>
      <Button
        onClick={() => (isAuthenticated ? handleCheckout() : redirectToLogin())}
        styleType={btnStyleType}
        className={containerClass}
        disabled={loading}
        juneSoProps={juneSoProps}
      >
        {btnText ?? <span>or purchase {packageName}</span>}
      </Button>
      {modalData && (
        <PackageAssociatedModal closeModal={() => setModalData(null)} userData={modalData} />
      )}
    </>
  );
};

export default PurchasePackageBtn;
